const m = require('minthril');
const { component } = require('../../../component');
const { Breadcrumbs } = require('../../../components/breadcrumbs');

const NextComparisonDocsPage = component(() => ({
  view: () => m('article.panel.docs-page.next-comparison', [
    m(Breadcrumbs, {
      items: [
        { label: 'Docs', href: '/docs' },
        { label: 'Comparisons', href: '/docs/comparisons' },
        { label: 'Next.js: Mid Engine Co' }
      ]
    }),
    m('h2', 'Building Mid Engine Co with Next.js'),
    m('p',
      'Mid Engine Co is a custom storefront backed by Shopify. Its browser app loads public product data, ' +
      'manages each visitor’s cart, and sends buyers to Shopify checkout. A Next.js version could render ' +
      'product pages on the server while keeping the cart interactive in the browser.'
    ),
    m('h3', 'A likely Next.js architecture'),
    m('ol', [
      m('li', [
        m('strong', 'Use App Router pages for products and collections. '),
        'Server Components can query Shopify and produce the initial product HTML.'
      ]),
      m('li', [
        m('strong', 'Use Client Components for the cart. '),
        'The cart uses the current visitor’s Shopify cart identity and updates in response to their actions.'
      ]),
      m('li', [
        m('strong', 'Send buyers to Shopify checkout. '),
        'The cart flow hands off using Shopify’s checkout URL.'
      ])
    ]),
    m('p', [
      'Next.js supports composing server-rendered components with browser-interactive components. ',
      m('a', { href: 'https://nextjs.org/docs/app/getting-started/server-and-client-components' }, 'Server and Client Components'),
      ' · ',
      m('a', { href: 'https://nextjs.org/docs/app/getting-started/fetching-data' }, 'Fetching data'),
      ' · ',
      m('a', { href: 'https://nextjs.org/docs/app/guides/caching' }, 'Caching')
    ]),
    m('h3', 'Decisions the Next.js implementation needs'),
    m('ul', [
      m('li', [
        m('strong', 'Which code runs on the server and which runs in the browser. '),
        'Product pages can fetch Shopify data in Server Components; cart controls need Client Components for state, events, and browser storage. Data passed across that boundary must be serializable.'
      ]),
      m('li', [
        m('strong', 'How product routes are created. '),
        'Define dynamic routes such as app/products/[handle], then choose whether handles are generated at build time with generateStaticParams or rendered when first requested.'
      ]),
      m('li', [
        m('strong', 'How Shopify responses and rendered pages are cached. '),
        'Set an explicit cache policy for catalog requests and pages. Decide how freshness, stale data, and upstream Shopify failures should affect a visitor’s response.'
      ]),
      m('li', [
        m('strong', 'How product changes invalidate pages. '),
        'A Shopify webhook can call a protected Route Handler that uses revalidateTag or revalidatePath. The webhook, secret, and mapping from changed products to affected pages must be implemented.'
      ]),
      m('li', [
        m('strong', 'How visitor-specific cart data stays private. '),
        'Keep cart operations in a Client Component, or make any server-side cart fetch session-aware and ensure it cannot enter a shared cache.'
      ]),
      m('li', [
        m('strong', 'How the server-rendered app is deployed. '),
        'Choose a compatible Node.js or platform runtime and configure server-only Shopify credentials. A static export is another option, but it cannot provide request-time server rendering.'
      ]),
      m('li', [
        m('strong', 'How much of the current storefront is rebuilt. '),
        'Mid Engine Co’s existing browser app and components would need to be moved into the React and Next.js structure, with behavior tested again across product, cart, and checkout flows.'
      ])
    ]),
    m('h3', 'Technical considerations'),
    m('p',
      'Next.js provides mechanisms for each concern, but the application still needs to connect them. ' +
      'Spache’s “Automatic” means the existing SPA flow handles the concern without implementing a Next.js ' +
      'rendering layer. JSON GET responses are captured automatically; capture rules are only needed for ' +
      'non-GET requests. Catalog freshness remains configurable.'
    ),
    m('div.next-comparison__table-wrap', [
      m('table.next-comparison__table', [
        m('thead', m('tr', [
          m('th', 'Technical consideration'),
          m('th', 'Next.js implementation'),
          m('th', 'Spache implementation')
        ])),
        m('tbody', [
          m('tr', [
            m('th', 'Product data in crawlable HTML'),
            m('td', 'Fetch catalog data in Server Components and render product markup into the response.'),
            m('td', 'Automatic')
          ]),
          m('tr', [
            m('th', 'Product and collection routes'),
            m('td', 'Create dynamic App Router segments; use generateStaticParams when product paths should be generated during the build.'),
            m('td', 'Automatic')
          ]),
          m('tr', [
            m('th', 'Catalog freshness'),
            m('td', 'Configure fetch/page caching, then use webhook-driven revalidateTag or revalidatePath when Shopify content changes.'),
            m('td', 'Automatic, configurable')
          ]),
          m('tr', [
            m('th', 'Visitor-specific cart'),
            m('td', 'Implement cart behavior in a Client Component or session-aware server action.'),
            m('td', 'Automatic')
          ]),
          m('tr', [
            m('th', 'Keep personal data out of shared HTML'),
            m('td', 'Keep cart data client-side or explicitly isolate personalized server responses from shared caching.'),
            m('td', 'Automatic')
          ]),
          m('tr', [
            m('th', 'Server/client component boundary'),
            m('td', 'Choose Server versus Client Components and pass the required state across their boundary.'),
            m('td', 'Automatic')
          ]),
          m('tr', [
            m('th', 'Rendering deployment'),
            m('td', 'Deploy a Next.js server runtime, or limit the app to what can be statically exported.'),
            m('td', 'Automatic')
          ]),
          m('tr', [
            m('th', 'Move the existing storefront'),
            m('td', 'Port the SPA UI and retest its commerce flows in the Next.js app structure.'),
            m('td', 'Automatic — keep the existing SPA')
          ])
        ])
      ])
    ]),
    m('p',
      'Next.js is a capable choice when you want to build the storefront around React Server Components and ' +
      'request-time or build-time rendering. Spache is a different trade: keep the current browser app and ' +
      'prepare its public rendered pages without migrating the storefront into a server-rendered framework.'
    )
  ])
}));

module.exports = { NextComparisonDocsPage };
